import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSession } from '@/hooks/useSession'
import { useLockStatus } from '@/hooks/useLockStatus'
import { useToast } from '@/components/ui/Toast'
import { Spinner } from '@/components/ui/Spinner'
import { LangSwitcher } from '@/components/ui/LangSwitcher'
import { fetchChampionPick, submitChampionPick } from '@/api/champion'
import { useLocalizedTeamName } from '@/utils/teamNames'
import type { Team } from '@/types/bracket'

export function ChampionPickPage() {
  const { t } = useTranslation()
  const getTeamName = useLocalizedTeamName()
  const session = useSession()
  const { isLocked } = useLockStatus('R32')
  const navigate = useNavigate()
  const { toast } = useToast()

  const [teams, setTeams] = useState<Team[]>([])
  const [currentPick, setCurrentPick] = useState<string | null>(null)
  const [selected, setSelected] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [search, setSearch] = useState('')

  useEffect(() => {
    if (!session.token) { navigate('/join'); return }
    fetchChampionPick(session.token)
      .then(r => {
        setTeams(r.teams)
        setCurrentPick(r.pick?.team_id ?? null)
        setSelected(r.pick?.team_id ?? null)
      })
      .catch(() => toast(t('common.failedToLoad'), 'error'))
      .finally(() => setLoading(false))
  }, [session.token])

  async function handleSave() {
    if (!selected || !session.token) return
    setSaving(true)
    try {
      await submitChampionPick(selected, session.token)
      setCurrentPick(selected)
      toast(t('champion.saved'), 'success')
    } catch {
      toast(t('common.error'), 'error')
    } finally {
      setSaving(false)
    }
  }

  const filtered = teams.filter(tm =>
    getTeamName(tm.id, tm.name).toLowerCase().includes(search.toLowerCase()) ||
    tm.name.toLowerCase().includes(search.toLowerCase()) ||
    tm.id.toLowerCase().includes(search.toLowerCase())
  )

  const grouped = filtered.reduce<Record<string, Team[]>>((acc, tm) => {
    const key = tm.group_label ?? 'Other'
    acc[key] = [...(acc[key] ?? []), tm]
    return acc
  }, {})

  const selectedTeam = teams.find(tm => tm.id === selected)
  const isDirty = selected !== currentPick
  const currentPickTeam = teams.find(tm => tm.id === currentPick)

  return (
    <div className="min-h-screen">
      <div className="border-b border-white/10 bg-pitch-900/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🏆</span>
            <div>
              <h1 className="text-base font-black text-white leading-tight">{t('champion.title')}</h1>
              <p className="text-[11px] text-white/40">{t('champion.subtitle')}</p>
            </div>
          </div>
          <nav className="flex gap-2 items-center">
            <LangSwitcher />
            <a href="/champion-board" className="btn-secondary btn-sm text-sm">{t('champion.board')}</a>
            <a href="/bracket" className="btn-secondary btn-sm text-sm">⚽ {t('nav.bracket')}</a>
            <a href="/leaderboard" className="btn-secondary btn-sm text-sm">🏅 {t('nav.leaderboard')}</a>
          </nav>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="card p-4 mb-6 bg-gold-400/5 border-gold-400/20">
          <div className="flex items-start gap-3">
            <span className="text-2xl">🎯</span>
            <div>
              <p className="text-sm font-semibold text-gold-400 mb-1">{t('champion.infoTitle')}</p>
              <p className="text-xs text-white/50">
                {t('champion.infoDescPre')} <span className="text-white font-medium">20 {t('champion.points')}</span> {t('champion.infoDescPost')}
                {isLocked && <span className="text-red-400 ml-1">{t('champion.locked')}</span>}
              </p>
            </div>
          </div>
        </div>

        {currentPick && (
          <div className="card p-3 mb-4 flex items-center gap-3 border-emerald-500/30 bg-emerald-500/5">
            <span className="text-2xl">{currentPickTeam?.flag_emoji ?? '🏳'}</span>
            <div>
              <p className="text-xs text-white/40">{t('champion.currentPick')}</p>
              <p className="text-sm font-bold text-emerald-400">
                {getTeamName(currentPickTeam?.id ?? null, currentPickTeam?.name ?? null)}
              </p>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20"><Spinner size="lg" /></div>
        ) : (
          <>
            <input
              className="input mb-4"
              placeholder={t('champion.search')}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />

            <div className="flex flex-col gap-4 mb-6">
              {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([group, grpTeams]) => (
                <div key={group}>
                  <p className="text-xs font-bold text-white/30 uppercase tracking-widest mb-2">
                    {t('champion.group', { name: group })}
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {grpTeams.map(tm => {
                      const isSelected = selected === tm.id
                      return (
                        <button
                          key={tm.id}
                          onClick={() => !isLocked && setSelected(tm.id)}
                          disabled={isLocked}
                          className={`flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
                            isSelected
                              ? 'bg-gold-400/15 border-gold-400/40 text-white'
                              : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10 hover:border-white/20'
                          } ${isLocked ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                          <span className="text-2xl">{tm.flag_emoji ?? '🏳'}</span>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold truncate">{getTeamName(tm.id, tm.name)}</p>
                            <p className="text-[10px] text-white/30">{tm.id}</p>
                          </div>
                          {isSelected && <span className="ml-auto text-gold-400 text-lg">✓</span>}
                        </button>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>

            {!isLocked && (
              <div className="sticky bottom-4">
                <button
                  onClick={handleSave}
                  disabled={!selected || !isDirty || saving}
                  className="btn-primary w-full py-3 text-base"
                >
                  {saving ? <Spinner size="sm" /> : <span>🏆</span>}
                  {selectedTeam
                    ? t('champion.pickBtn', { flag: selectedTeam.flag_emoji ?? '', name: getTeamName(selectedTeam.id, selectedTeam.name) })
                    : t('champion.selectTeam')}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
